import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';

import { EntitySelect } from '@/components/EntitySelect';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { FormSection } from '@/components/ui/form-section';
import { InfoHint } from '@/components/ui/info-hint';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import type { ApiError } from '@/core/api';
import {
  createDepartmentOption,
  resolveDepartmentLabel,
  searchDepartments,
} from '@/features/hrm/employees';
import { useProjects } from '@/features/projects/queries';
import { cn } from '@/lib/utils';
import { useInviteMember, useRoles } from '../queries';

const NO_PROJECT = 'none';

const schema = z.object({
  email: z.string().trim().min(1, 'Email is required').email('Enter a valid email'),
  first_name: z.string().max(150),
  last_name: z.string().max(150),
  type: z.enum(['staff', 'guest']),
  roles: z.array(z.number()),
  department: z.string(),
  /** Project public_id, or NO_PROJECT sentinel. */
  project: z.string(),
  white_label: z.boolean(),
  email_verified: z.boolean(),
});

type FormValues = z.infer<typeof schema>;

const FIELD_KEYS = [
  'email', 'first_name', 'last_name', 'type', 'roles', 'department', 'project',
  'white_label', 'email_verified',
] as const;

interface MemberInviteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function MemberInviteDialog({ open, onOpenChange }: MemberInviteDialogProps) {
  const rolesQuery = useRoles();
  const invite = useInviteMember();
  // Projects populate the guest auto-assign picker (small, one-page list is fine).
  const projectsQuery = useProjects({ ordering: 'name' });

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      email: '', first_name: '', last_name: '', type: 'staff', roles: [], department: '',
      project: NO_PROJECT, white_label: false, email_verified: false,
    },
  });

  const isGuest = useWatch({ control: form.control, name: 'type' }) === 'guest';

  async function handleSubmit(values: FormValues) {
    const guest = values.type === 'guest';
    try {
      await invite.mutateAsync({
        email: values.email.trim(),
        first_name: values.first_name,
        last_name: values.last_name,
        type: values.type,
        // Guests never carry staff roles or a department (ADR-004).
        roles: guest ? [] : values.roles,
        department: guest || !values.department ? null : Number(values.department),
        // Project auto-assign + white-label are guest-only controls.
        project: guest && values.project !== NO_PROJECT ? values.project : null,
        white_label: guest ? values.white_label : false,
        email_verified: values.email_verified,
      });
      form.reset();
      onOpenChange(false);
    } catch (e) {
      const err = e as ApiError;
      if (err?.fieldErrors) {
        for (const [field, messages] of Object.entries(err.fieldErrors)) {
          const key = FIELD_KEYS.find((k) => k === field);
          form.setError(key ?? 'root', { message: messages.join(' ') });
        }
      }
    }
  }

  const submitting = form.formState.isSubmitting;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Add member</DialogTitle>
          <DialogDescription>
            Invite someone to this organization. New people set their password via the
            reset link on the sign-in screen.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
            <FormSection title="Person" description="Who are you adding?">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input type="email" placeholder="person@company.com" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="grid gap-4 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="first_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>First name</FormLabel>
                      <FormControl>
                        <Input placeholder="Optional" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="last_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Last name</FormLabel>
                      <FormControl>
                        <Input placeholder="Optional" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="email_verified"
                render={({ field }) => (
                  <FormItem className="flex items-center justify-between gap-4">
                    <div className="space-y-0.5">
                      <FormLabel className="flex items-center gap-1.5">
                        Email already verified
                        <InfoHint text="Marks their email as verified up front — skips the confirmation step (e.g. SSO or trusted invitees). They still set a password via the invite." />
                      </FormLabel>
                      <p className="text-muted-foreground text-sm">
                        Skip email confirmation for this person.
                      </p>
                    </div>
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                  </FormItem>
                )}
              />
            </FormSection>

            <FormSection title="Access" description="Membership type and roles.">
              <FormField
                control={form.control}
                name="type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Type</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="staff">Staff</SelectItem>
                        <SelectItem value="guest">Guest (client)</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {isGuest ? (
                <>
                  <p className="text-muted-foreground text-sm">
                    Guests get scoped, read-only access to their own company — no staff
                    roles.
                  </p>

                  <FormField
                    control={form.control}
                    name="project"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="flex items-center gap-1.5">
                          Project
                          <InfoHint text="Optional — auto-assigns the guest to this project as a viewer." />
                        </FormLabel>
                        <Select value={field.value} onValueChange={field.onChange}>
                          <FormControl>
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder="No project" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value={NO_PROJECT}>No project</SelectItem>
                            {projectsQuery.data?.results.map((p) => (
                              <SelectItem key={p.public_id} value={p.public_id}>
                                {p.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="white_label"
                    render={({ field }) => (
                      <FormItem className="flex items-center justify-between gap-4">
                        <div className="space-y-0.5">
                          <FormLabel className="flex items-center gap-1.5">
                            White label
                            <InfoHint text="Hides your organization name from the invite email — it references the project (or Dev13Systems) instead." />
                          </FormLabel>
                          <p className="text-muted-foreground text-sm">
                            Keep the org name hidden from this client.
                          </p>
                        </div>
                        <FormControl>
                          <Switch checked={field.value} onCheckedChange={field.onChange} />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </>
              ) : (
                <>
                <FormField
                  control={form.control}
                  name="roles"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-1.5">
                        Roles
                        <InfoHint text="Optional — controls what this person can see and do." />
                      </FormLabel>
                      <FormControl>
                        <div className="flex flex-wrap gap-2" role="group" aria-label="Roles">
                          {rolesQuery.isPending ? (
                            <span className="text-muted-foreground text-sm">Loading roles…</span>
                          ) : (
                            rolesQuery.data
                              ?.filter((r) => r.title !== 'Guest')
                              .map((role) => {
                                const selected = field.value.includes(role.id);
                                return (
                                  <button
                                    key={role.id}
                                    type="button"
                                    aria-pressed={selected}
                                    onClick={() =>
                                      field.onChange(
                                        selected
                                          ? field.value.filter((id) => id !== role.id)
                                          : [...field.value, role.id],
                                      )
                                    }
                                    className={cn(
                                      'rounded-full border px-3 py-1 text-sm transition-colors',
                                      selected
                                        ? 'bg-primary text-primary-foreground border-primary'
                                        : 'border-input hover:bg-accent',
                                    )}
                                  >
                                    {role.title}
                                  </button>
                                );
                              })
                          )}
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="department"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-1.5">
                        Department
                        <InfoHint text="Creates their employee record in this department." />
                      </FormLabel>
                      <FormControl>
                        <EntitySelect
                          queryKey="department-picker"
                          value={field.value}
                          onChange={field.onChange}
                          search={searchDepartments}
                          resolveLabel={resolveDepartmentLabel}
                          onCreate={createDepartmentOption}
                          placeholder="No department"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                </>
              )}
            </FormSection>

            {form.formState.errors.root ? (
              <p className="text-destructive text-sm">{form.formState.errors.root.message}</p>
            ) : null}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? 'Adding…' : 'Add member'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
